import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getSaludEmpresa } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Wrench,
  ChevronDown,
  Database,
  Calculator,
  TrendingUp,
} from "lucide-react";

// Config visual por estado de un item / área.
const estadoConfig = {
  ok: {
    icon: CheckCircle2,
    label: "OK",
    textColor: "text-emerald-600",
    bg: "bg-emerald-100",
    badge: "bg-emerald-500 hover:bg-emerald-600",
  },
  warning: {
    icon: AlertTriangle,
    label: "Aviso",
    textColor: "text-amber-600",
    bg: "bg-amber-100",
    badge: "bg-amber-500 hover:bg-amber-600",
  },
  error: {
    icon: XCircle,
    label: "Error",
    textColor: "text-red-600",
    bg: "bg-red-100",
    badge: "bg-red-500 hover:bg-red-600",
  },
  na: {
    icon: HelpCircle,
    label: "N/A",
    textColor: "text-gray-500",
    bg: "bg-gray-100",
    badge: "bg-gray-400 hover:bg-gray-500",
  },
};

// Iconos sugeridos por área (por su clave). Si no coincide, usa Activity.
const areaIcons = {
  datos: Database,
  importacion: Database,
  contable: Calculator,
  proyeccion: TrendingUp,
};

// Cabecera semáforo global según el estado de la empresa.
const globalConfig = {
  ok: {
    icon: CheckCircle2,
    titulo: "Todo en orden",
    textColor: "text-emerald-600",
    bg: "bg-emerald-100",
    border: "border-l-emerald-500",
  },
  warning: {
    icon: AlertTriangle,
    titulo: "Hay avisos",
    textColor: "text-amber-600",
    bg: "bg-amber-100",
    border: "border-l-amber-500",
  },
  error: {
    icon: XCircle,
    titulo: "Hay errores",
    textColor: "text-red-600",
    bg: "bg-red-100",
    border: "border-l-red-500",
  },
};

function SaludItem({ item }) {
  const navigate = useNavigate();
  const config = estadoConfig[item.estado] || estadoConfig.na;
  const Icon = config.icon;
  const esOk = item.estado === "ok";

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
        esOk ? "bg-muted/20 opacity-70" : "bg-muted/40 hover:bg-muted/60"
      }`}
    >
      <div className={`p-2 rounded-full shrink-0 ${config.bg}`}>
        <Icon className={`h-4 w-4 ${config.textColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{item.titulo}</span>
        </div>
        {item.mensaje && (
          <p className="text-sm text-muted-foreground mt-0.5">{item.mensaje}</p>
        )}
        {item.solucion && !esOk && (
          <div className="mt-2 text-xs rounded-md bg-blue-50/70 border border-blue-200 p-2 dark:bg-blue-950/40 dark:border-blue-900">
            <p className="font-medium text-blue-900 dark:text-blue-300 flex items-center gap-1 mb-0.5">
              <Wrench className="h-3 w-3" /> Cómo arreglarlo
            </p>
            <p className="text-blue-700 dark:text-blue-400">{item.solucion}</p>
          </div>
        )}
        {item.link && !esOk && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => navigate(item.link)}
            data-testid={`salud-item-link-${item.id}`}
          >
            Ir a arreglarlo
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

function AreaCard({ area }) {
  const config = estadoConfig[area.estado] || estadoConfig.na;
  const Icon = config.icon;
  const AreaIcon = areaIcons[area.area] || Activity;

  const items = area.items || [];
  const itemsProblema = items.filter((i) => i.estado !== "ok");
  const itemsOk = items.filter((i) => i.estado === "ok");

  // Los items OK se muestran colapsados por defecto.
  const [mostrarOk, setMostrarOk] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <AreaIcon className="h-5 w-5 text-muted-foreground" />
            {area.titulo}
          </CardTitle>
          <Badge className={`${config.badge} text-xs text-white`}>
            <Icon className="h-3 w-3 mr-1" />
            {config.label}
          </Badge>
        </div>
        <CardDescription>
          {itemsProblema.length > 0
            ? `${itemsProblema.length} ${
                itemsProblema.length === 1 ? "punto requiere atención" : "puntos requieren atención"
              }`
            : "Sin incidencias en esta área"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay comprobaciones en esta área.
          </p>
        )}

        {/* Primero los items con incidencia */}
        {itemsProblema.map((item) => (
          <SaludItem key={item.id} item={item} />
        ))}

        {/* Items OK colapsables */}
        {itemsOk.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setMostrarOk((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors pt-1"
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${mostrarOk ? "rotate-180" : ""}`}
              />
              {mostrarOk ? "Ocultar" : "Mostrar"} {itemsOk.length}{" "}
              {itemsOk.length === 1 ? "comprobación en orden" : "comprobaciones en orden"}
            </button>
            {mostrarOk && (
              <div className="space-y-2">
                {itemsOk.map((item) => (
                  <SaludItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function SaludEmpresaPage() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadSalud();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  const loadSalud = async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(false);
      const res = await getSaludEmpresa(empresaId);
      setData(res.data);
      if (silent) toast.success("Salud actualizada");
    } catch (e) {
      setError(true);
      toast.error(
        e.response?.data?.detail || "Error al cargar la salud de la empresa"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-16 text-muted-foreground">
          <XCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="mb-4">No se pudo cargar la salud de la empresa</p>
          <Button variant="outline" onClick={() => loadSalud()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  const resumen = data.resumen || { ok: 0, warning: 0, error: 0 };
  const gConfig = globalConfig[data.estado] || globalConfig.ok;
  const GlobalIcon = gConfig.icon;
  const areas = data.areas || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="salud-empresa-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/empresas/${empresaId}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Activity className="h-7 w-7 text-primary" />
              Salud de la empresa
            </h1>
            <p className="text-muted-foreground mt-1">
              {data.empresa_nombre || "Diagnóstico general"}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadSalud({ silent: true })}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {/* Semáforo global */}
      <Card className={`border-l-4 ${gConfig.border}`}>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${gConfig.bg}`}>
                <GlobalIcon className={`h-7 w-7 ${gConfig.textColor}`} />
              </div>
              <div>
                <div className={`text-xl font-bold ${gConfig.textColor}`}>
                  {gConfig.titulo}
                </div>
                <div className="text-sm text-muted-foreground">
                  Estado general de la empresa
                </div>
              </div>
            </div>

            {/* Resumen X ok, Y avisos, Z errores */}
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">{resumen.ok}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> OK
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{resumen.warning}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Avisos
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{resumen.error}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <XCircle className="h-3 w-3" /> Errores
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Áreas */}
      {areas.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <HelpCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No hay áreas de diagnóstico disponibles</p>
        </div>
      ) : (
        <div className="space-y-4">
          {areas.map((area) => (
            <AreaCard key={area.area} area={area} />
          ))}
        </div>
      )}
    </div>
  );
}
